<template>
    <information-modal
        title="Delete"
        key="confirmDialog"
        ref="confirmDialog"
        :show-modal="isShowDeleteDialog"
        :show-footer="true"
        className="confirm"
        @dismiss="hideDeleteConfirmEditor">
        Are You Sure?
        <template v-slot:footer>
            <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal">
                No
            </button>

            <button
                type="button"
                class="btn btn-primary"
                @click.prevent="deleteDocument">
                Yes
            </button>
        </template>
    </information-modal>

    <information-modal
        title="Create Document Template"
        :show-modal="isShowCreateEditor"
        ref="createDialog"
        @dismiss="hideCreateEditor">
        <form @submit.prevent="createDocumentTemplate">
            <fieldset class="border-bottom mb-3">
                <div class="mb-3 row">
                    <label
                        for="documentTemplateName"
                        class="col-sm-3 col-form-label">
                        Name:</label
                    >
                    <div class="col-sm-9">
                        <text-input
                            id="documentTemplateName"
                            name="documentTemplateName"
                            type="text"
                            v-model="documentTemplate.name"
                            :error="errors.name" />
                    </div>
                </div>

                <div class="mb-3 row">
                    <label
                        for="documentTemplateDescription"
                        class="col-sm-3 col-form-label"
                        >Description:</label
                    >
                    <div class="col-sm-9">
                        <textarea
                            class="form-control"
                            id="documentTemplateDescription"
                            v-model="documentTemplate.description"
                            :error="errors.description" />
                    </div>
                </div>
                <div class="mb-3 row">
                    <label
                        for="documentTemplateFile"
                        class="col-sm-3 col-form-label"
                        >File:</label
                    >
                    <div class="col-sm-9">
                        <file-input
                            v-model="documentTemplate.file"
                            name="documentTemplateFile"
                            :error="errors.file"></file-input>
                    </div>
                </div>
            </fieldset>
            <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">
                    Cancel
                </button>
                <button
                    class="btn btn-primary"
                    type="submit">
                    Save
                </button>
            </div>
        </form>
    </information-modal>

    <information-modal
        title="Replace Document"
        :show-modal="isShowReplaceDocument"
        ref="createDialog"
        @dismiss="hideReplaceDocument">
        <form @submit.prevent="replaceDocumentTemplate">
            <fieldset class="border-bottom mb-3">
                <div class="mb-3 row">
                    <label
                        for="replaceDocumentTemplateFile"
                        class="col-sm-3 col-form-label"
                        >File:</label
                    >
                    <div class="col-sm-9">
                        <file-input
                            v-model="replaceDocument.file"
                            name="replaceDocumentTemplateFile"
                            :error="errors.file"></file-input>
                    </div>
                </div>
            </fieldset>
            <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">
                    Cancel
                </button>
                <button
                    class="btn btn-primary"
                    type="submit">
                    Save
                </button>
            </div>
        </form>
    </information-modal>
    <system-alerts></system-alerts>
    <div class="document-templates-grid">
        <b-grid
            caption="List of document templates"
            :tableOptions="auditDocumentTemplateStore.tableOptions"
            :filterableFields="auditDocumentTemplateStore.filterableFields"
            :filterOptions="auditDocumentTemplateStore.filterOptions"
            @optionChange="getData"
            :items="auditDocumentTemplateStore.items"
            :columns="columns"
            :loading="auditDocumentTemplateStore.loading">
            <template #beforeFilter>
                <button
                    @click.prevent="showCreateEditor"
                    v-if="canAdd"
                    class="btn btn-primary">
                    Create Document Template
                </button>
            </template>
            <template #cell.actions="{item}">
                <template v-if="item.fileId">
                    <a
                        class="btn btn-sm btn-secondary me-2"
                        :href="`api/v1/audit/file/${item.fileId}`"
                        >View Document</a
                    >
                </template>

                <button
                    @click.prevent="showReplaceDocument(item.id)"
                    aria-label="Replace Document"
                    type="button"
                    v-if="canDelete"
                    class="btn btn-sm btn-primary me-2">
                    Edit
                </button>

                <button
                    @click.prevent="deleteDocumentConfirm(item.id)"
                    type="button"
                    v-if="canDelete"
                    class="btn btn-sm btn-danger">
                    Delete
                </button>
                <span v-else></span>
            </template>
        </b-grid>
    </div>
</template>
<script lang="ts" setup>
import {ref, reactive, computed} from "vue";
import BGrid from "./BGrid/BGrid.vue";
import type {BTableColumn} from "./BTable/BTable.types";
import InformationModal from "./Misc/InformationModal.vue";
import SystemAlerts from "./Misc/SystemAlerts.vue";
import "@vuepic/vue-datepicker/dist/main.css";
import type {DocumentTemplate, DocumentTemplateUpdateRequest} from "../audit.types";
import {useFormValidation} from "../validation";
import TextInput from "./Fields/TextInput.vue";
import FileInput from "./Fields/FileInput.vue";
import {useAuditDocumentTemplateStore} from "@/stores/auditDocumentTemplateStore";
import {object, string, date, mixed} from "yup";

const props = defineProps<{
    title: String;
    runAudits: boolean;
    createAudits: boolean;
}>();

const documentTemplate = ref<DocumentTemplate>({} as DocumentTemplate);
const isShowCreateEditor = ref(false);
const auditDocumentTemplateStore = useAuditDocumentTemplateStore();

const schema = object<DocumentTemplate>({
    name: string().required().max(50).label("Name"),
    description: string().nullable().max(500).label("Description"),
    file: mixed().required().label("File"),
});

const columns = reactive<BTableColumn[]>([
    {
        key: "name",
        text: "Name",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item["name"],
    },
    {
        key: "description",
        text: "Description",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item["description"],
    },
    {
        key: "UpdatedBy",
        text: "Updated By",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.updatedBy,
    },
    {
        key: "UpdatedOn",
        text: "Updated Date",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.updatedOn,
    },
    {
        key: "actions",
        text: "",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap text-end"],
        value: (item) => item["link"],
    },
]);

const showCreateEditor = () => {
    resetValidation();
    documentTemplate.value = {} as DocumentTemplate;
    isShowCreateEditor.value = true;
};
const hideCreateEditor = () => {
    isShowCreateEditor.value = false;
};

const getData = async () => {
    await auditDocumentTemplateStore.list();
};

const canAdd = computed(() => props.createAudits);

const canDelete = (): boolean => props.runAudits;

const isShowDeleteDialog = ref(false);
const selectedId = ref(0);

const hideDeleteConfirmEditor = async function () {
    isShowDeleteDialog.value = false;
};

const deleteDocument = async () => {
    await auditDocumentTemplateStore.deleteItem(selectedId.value);
    hideDeleteConfirmEditor();
    getData();
};

const deleteDocumentConfirm = async (id: number) => {
    selectedId.value = id;
    isShowDeleteDialog.value = true;
};

const isShowReplaceDocument = ref(false);

const replaceDocumentSchema = object<DocumentTemplate>({
    file: mixed().required().label("File"),
});
const replaceDocument = ref<DocumentTemplateUpdateRequest>({} as DocumentTemplateUpdateRequest);
const hideReplaceDocument = async () => {
    isShowReplaceDocument.value = false;
};
const showReplaceDocument = async (id: number) => {
    resetValidation();
    replaceDocument.value = {} as DocumentTemplateUpdateRequest;
    isShowReplaceDocument.value = true;
    replaceDocument.value.id = id;
};

const replaceDocumentTemplate = async () => {
    if (await handleSubmit(replaceDocumentSchema, replaceDocument.value)) {
        await auditDocumentTemplateStore.replace(replaceDocument.value);
        await getData();
        await hideReplaceDocument();
    }
};

const {handleSubmit, errors, resetValidation} = useFormValidation();

const createDocumentTemplate = async () => {
    if (await handleSubmit(schema, documentTemplate.value)) {
        await auditDocumentTemplateStore.create(documentTemplate.value);
        await getData();
        await hideCreateEditor();
        documentTemplate.value = {} as DocumentTemplate;
    }
};
await getData();
</script>
